import type { PageHelp } from '../../help/types';

/** Guide for `/search` — the key search across every project and package. */
export const searchHelp: PageHelp = {
  title: 'Key search',
  intro:
    'Finds keys in every project you can read and in every package, wherever you are in Glossa. The page only finds keys: open one and it is edited in the keys list of its project or package.',
  sections: ['Search', 'Results'],
  tips: [
    'The search box in the top bar (Ctrl+K) searches the same way and shows the best eight hits; Enter opens the first one.',
    'The address of this page includes the query and the filters, so a search can be shared as a link.',
  ],
  steps: [
    // ── Search ──────────────────────────────────────────────────────────────────
    {
      selector: '[data-help="search-query"]',
      title: 'Type what you are looking for',
      body: 'Part of a key name, a description, or a text as the application shows it. The search starts once you have typed two characters and paused for half a second; upper and lower case do not matter.',
      section: 'Search',
      i18n: {
        'de-AT': {
          title: 'Eingeben, wonach Sie suchen',
          body: 'Ein Teil eines Key-Namens, einer Beschreibung oder eines Textes, wie ihn die Anwendung anzeigt. Die Suche beginnt, sobald Sie zwei Zeichen eingegeben und eine halbe Sekunde pausiert haben; Groß- und Kleinschreibung spielen keine Rolle.',
        },
        'sr-Latn-RS': {
          title: 'Unos onoga što tražite',
          body: 'Deo imena ključa, opisa ili teksta kakav aplikacija prikazuje. Pretraga počinje kada unesete dva znaka i zastanete pola sekunde; velika i mala slova nisu bitna.',
        },
      },
    },
    {
      selector: '[data-help="search-match"]',
      title: 'Choose where to search',
      body: 'By default both key names (with descriptions) and values are searched. Narrow it to names when you know part of the key, or to values when you only know the text.',
      section: 'Search',
      i18n: {
        'de-AT': {
          title: 'Festlegen, wo gesucht wird',
          body: 'Standardmäßig werden Key-Namen (samt Beschreibungen) und Werte durchsucht. Schränken Sie auf Namen ein, wenn Sie einen Teil des Keys kennen, oder auf Werte, wenn Sie nur den Text kennen.',
        },
        'sr-Latn-RS': {
          title: 'Izbor mesta pretrage',
          body: 'Podrazumevano se pretražuju i imena ključeva (sa opisima) i vrednosti. Suzite na imena kada znate deo ključa, ili na vrednosti kada znate samo tekst.',
        },
      },
    },
    {
      selector: '[data-help="search-owner"]',
      title: 'Limit the search to projects or packages',
      body: 'Projects are the ones you have a role in — administrators see all of them. Packages are open to everybody.',
      section: 'Search',
      i18n: {
        'de-AT': {
          title: 'Die Suche auf Projekte oder Pakete beschränken',
          body: 'Projekte sind jene, in denen Sie eine Rolle haben — Administratoren sehen alle. Pakete sind für alle zugänglich.',
        },
        'sr-Latn-RS': {
          title: 'Ograničavanje pretrage na projekte ili pakete',
          body: 'Projekti su oni u kojima imate ulogu — administratori vide sve. Paketi su dostupni svima.',
        },
      },
    },
    {
      selector: '[data-help="search-locale"]',
      title: 'Search the values of another language',
      body: 'Values are searched in each project’s or package’s source locale — the text the application shows before translation. Pick a locale here to search its translations instead, for example to find where a Serbian text comes from. Disabled while only key names are searched.',
      section: 'Search',
      i18n: {
        'de-AT': {
          title: 'Die Werte einer anderen Sprache durchsuchen',
          body: 'Werte werden in der Quellsprache des jeweiligen Projekts oder Pakets durchsucht — dem Text, den die Anwendung vor der Übersetzung zeigt. Wählen Sie hier eine Sprache, um stattdessen deren Übersetzungen zu durchsuchen, etwa um herauszufinden, woher ein serbischer Text stammt. Deaktiviert, solange nur Key-Namen durchsucht werden.',
        },
        'sr-Latn-RS': {
          title: 'Pretraga vrednosti na drugom jeziku',
          body: 'Vrednosti se pretražuju na izvornom jeziku svakog projekta ili paketa — tekstu koji aplikacija prikazuje pre prevoda. Ovde izaberite jezik da biste umesto toga pretražili njegove prevode, na primer da nađete odakle potiče neki srpski tekst. Onemogućeno dok se pretražuju samo imena ključeva.',
        },
      },
    },
    // ── Results ─────────────────────────────────────────────────────────────────
    {
      selector: '[data-help="search-results"]',
      title: 'Read the results',
      body: 'Best matches come first: a key named exactly like your query, then names that start with it, then names that contain it, then descriptions, then values. “Found in” says where the query matched, with the language for a value.',
      section: 'Results',
      i18n: {
        'de-AT': {
          title: 'Die Ergebnisse lesen',
          body: 'Die besten Treffer stehen oben: ein Key, der genau wie Ihre Suche heißt, dann Namen, die damit beginnen, dann Namen, die sie enthalten, dann Beschreibungen, dann Werte. „Found in“ zeigt, wo die Suche getroffen hat — bei einem Wert samt Sprache.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje rezultata',
          body: 'Najbolji pogoci su prvi: ključ koji se zove tačno kao vaš upit, zatim imena koja počinju njime, pa imena koja ga sadrže, pa opisi, pa vrednosti. „Found in“ pokazuje gde je upit pronađen, uz jezik kada je reč o vrednosti.',
        },
      },
    },
    {
      selector: '[data-help="search-open"]',
      title: 'Open a key',
      body: 'Opens the keys list of the key’s project or package, filtered to that key, with its details already open. There you edit it like any other key, with the rights you have in that project or package.',
      section: 'Results',
      i18n: {
        'de-AT': {
          title: 'Einen Key öffnen',
          body: 'Öffnet die Key-Liste des Projekts oder Pakets, zu dem der Key gehört, auf diesen Key gefiltert und mit bereits geöffneten Details. Dort bearbeiten Sie ihn wie jeden anderen Key — mit den Rechten, die Sie in diesem Projekt oder Paket haben.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje ključa',
          body: 'Otvara listu ključeva projekta ili paketa kojem ključ pripada, filtriranu na taj ključ i sa već otvorenim detaljima. Tamo ga menjate kao i svaki drugi ključ — sa pravima koja imate u tom projektu ili paketu.',
        },
      },
    },
    {
      selector: '[data-help="search-linked-via"]',
      title: 'Keys you reach through a link',
      body: '“via” marks a key of a project you have no role in, which a project of yours links. It opens in your project; a change you make there becomes a suggestion for the owner unless you may translate in the owner’s project.',
      section: 'Results',
      i18n: {
        'de-AT': {
          title: 'Keys, die Sie über einen Link erreichen',
          body: '„via“ kennzeichnet einen Key eines Projekts, in dem Sie keine Rolle haben, den aber eines Ihrer Projekte verlinkt. Er öffnet sich in Ihrem Projekt; eine Änderung dort wird zu einem Vorschlag an den Eigentümer, sofern Sie im Projekt des Eigentümers nicht übersetzen dürfen.',
        },
        'sr-Latn-RS': {
          title: 'Ključevi do kojih stižete preko linka',
          body: '„via“ označava ključ projekta u kojem nemate ulogu, a koji linkuje neki vaš projekat. Otvara se u vašem projektu; izmena koju tamo napravite postaje predlog za vlasnika, osim ako smete da prevodite u projektu vlasnika.',
        },
      },
    },
    {
      selector: '[data-help="search-load-more"]',
      title: 'Load more results',
      body: 'Results come fifty at a time. The number next to the filters counts all of them.',
      section: 'Results',
      i18n: {
        'de-AT': {
          title: 'Weitere Ergebnisse laden',
          body: 'Ergebnisse kommen in Fünfzigerschritten. Die Zahl neben den Filtern zählt alle.',
        },
        'sr-Latn-RS': {
          title: 'Učitavanje još rezultata',
          body: 'Rezultati stižu po pedeset. Broj pored filtera broji sve rezultate.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Key-Suche',
      intro:
        'Findet Keys in jedem Projekt, das Sie lesen dürfen, und in jedem Paket — egal, wo Sie sich in Glossa befinden. Die Seite findet Keys nur: Öffnen Sie einen, und er wird in der Key-Liste seines Projekts oder Pakets bearbeitet.',
      sections: { Search: 'Suche', Results: 'Ergebnisse' },
      tips: [
        'Das Suchfeld in der oberen Leiste (Strg+K) sucht genauso und zeigt die acht besten Treffer; die Eingabetaste öffnet den ersten.',
        'Die Adresse dieser Seite enthält Suchbegriff und Filter, eine Suche lässt sich also als Link weitergeben.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Pretraga ključeva',
      intro:
        'Pronalazi ključeve u svakom projektu koji smete da čitate i u svakom paketu, gde god da se nalazite u Glossi. Stranica samo pronalazi ključeve: otvorite jedan i on se menja u listi ključeva svog projekta ili paketa.',
      sections: { Search: 'Pretraga', Results: 'Rezultati' },
      tips: [
        'Polje za pretragu u gornjoj traci (Ctrl+K) pretražuje na isti način i prikazuje osam najboljih pogodaka; Enter otvara prvi.',
        'Adresa ove stranice sadrži upit i filtere, pa se pretraga može poslati kao link.',
      ],
    },
  },
};
